import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from '../../hooks/useReducedMotion';
import { getActiveBanners, getActiveFlashSales, getCollections } from '../../utils/api';
import { normalizeBanners, pickLocalized } from '../../utils/banners';
import { withDeadlines } from '../../utils/flashSales';
import { dprCappedSizes, sizedImage, sizedSrcSet } from '../../utils/imageSize';
import { markHeroPhotoDone, writeHeroHint } from '../../utils/heroHint';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * Colourful promo hero (SHEIN / Noon style). Phones: big rounded cards in a
 * swipe track, the next card peeking in, autoplay every 5 s. Desktop: one
 * big card and two stacked beside it.
 *
 * Slides come only from real data, in this order: a live flash sale (the
 * only place "% OFF" may appear, and only with the server's
 * `max_discount_percentage`), the admin banners, the curated collections;
 * with none of them, category edits with plain headlines. Autoplay stops
 * on hover, focus, touch, the pause button and prefers-reduced-motion.
 */

// Brand crimson + Gulf jewel tones (owner, 29 Sep). White text on each is AA.
export const HERO_PALETTES = [
  { bg: 'bg-crimson', text: 'text-white', sub: 'text-white/90', cta: 'bg-white text-ink', dark: true },
  { bg: 'bg-jewel-emerald', text: 'text-white', sub: 'text-white/90', cta: 'bg-white text-ink', dark: true },
  { bg: 'bg-jewel-sapphire', text: 'text-white', sub: 'text-white/90', cta: 'bg-white text-ink', dark: true },
  { bg: 'bg-jewel-amethyst', text: 'text-white', sub: 'text-white/90', cta: 'bg-jewel-saffron text-ink', dark: true },
  { bg: 'bg-ink dark:ring-1 dark:ring-inset dark:ring-white/20', text: 'text-white', sub: 'text-white/90', cta: 'bg-white text-ink', dark: true },
];

// Without banners or collections: category edits, no numbers.
const FALLBACK_SLIDES = [
  { id: 'new-season', title: { en: 'New Season', ar: 'موسم جديد' }, line: { en: 'Fresh arrivals from the houses we love', ar: 'وصولات جديدة من الدور التي نحبها' }, cta: { en: 'Shop new in', ar: 'تسوق الجديد' }, href: '/products', image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200&q=70&auto=format' },
  { id: 'gift-edit', title: { en: 'The Gift Edit', ar: 'تشكيلة الهدايا' }, line: { en: 'Fine jewellery, wrapped for them', ar: 'مجوهرات راقية مغلفة لمن تحب' }, cta: { en: 'Shop gifts', ar: 'تسوق الهدايا' }, href: '/products?cat=jewelry', image: 'https://images.pexels.com/photos/1232931/pexels-photo-1232931.jpeg?w=1200&q=70&auto=format' },
  { id: 'evening', title: { en: 'Evening Glamour', ar: 'سهرات ساحرة' }, line: { en: 'Gowns, clutches and heels for the night', ar: 'فساتين وحقائب سهرة وكعوب للمساء' }, cta: { en: 'Shop evening', ar: 'تسوق السهرات' }, href: '/products?cat=women', image: 'https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=1200&q=70&auto=format' },
  { id: 'scent', title: { en: 'Signature Scents', ar: 'عطور مميزة' }, line: { en: 'Oud and fragrance to remember', ar: 'عود وعطور لا تُنسى' }, cta: { en: 'Shop scents', ar: 'تسوق العطور' }, href: '/products?cat=perfumes', image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=1200&q=70&auto=format' },
];

// Each collection card names its own edit ("Shop Eid Gifting") so the hero
// never repeats one label; a long or missing title falls back to "Shop the edit".
const collectionCta = (title) => {
  const name = (lang) => {
    const v = typeof title === 'string' ? (lang === 'en' ? title : '') : title?.[lang];
    const full = String(v || '').trim();
    // "The Evening Edit" → "Shop Evening Edit"; "The Edit" → "Shop the edit".
    const rest = full.replace(/^the\s+/i, '');
    const clean = rest === full || rest.includes(' ') ? rest : '';
    return clean.length && clean.length <= 18 ? clean : '';
  };
  return {
    en: name('en') ? `Shop ${name('en')}` : 'Shop the edit',
    ar: name('ar') ? `تسوق ${name('ar')}` : 'تسوق التشكيلة',
  };
};

const isHttp = (v) => typeof v === 'string' && /^https?:\/\//i.test(v.trim());

/**
 * Slides for the hero, from the real data only → [{id, title, line, cta,
 * href, image}] with texts as {en, ar} maps or strings.
 */
export const buildHeroSlides = ({ sale = null, banners = [], collections = [] } = {}) => {
  const slides = [];
  if (sale) {
    const percent = Math.max(0, Math.floor(Number(sale.max_discount_percentage) || 0));
    const image = (sale.products || []).map((p) => (Array.isArray(p?.images) ? p.images[0] : null)).find(isHttp) || '';
    slides.push({
      id: `sale-${sale.id}`,
      title: percent > 0 ? { en: `Up to ${percent}% OFF`, ar: `خصم حتى ${percent}٪` } : sale.name,
      line: percent > 0 ? sale.name : sale.subtitle,
      cta: { en: 'Shop the sale', ar: 'تسوق التخفيضات' },
      href: '/products?sale=true',
      image,
      sale: true,
    });
  }
  banners.forEach((b) => {
    slides.push({ id: `banner-${b.id}`, title: b.title, line: b.subtitle, cta: b.cta, href: b.href || '/products', image: b.image });
  });
  if (!banners.length) {
    (Array.isArray(collections) ? collections : []).forEach((c) => {
      if (!c || !c.slug) return;
      slides.push({
        id: `collection-${c.slug}`,
        title: c.title,
        line: c.subtitle,
        cta: collectionCta(c.title),
        href: `/collections/${encodeURIComponent(c.slug)}`,
        image: [c.hero_image_url, c.cover_image_url].find(isHttp) || '',
      });
    });
  }
  if (slides.length < 3) {
    FALLBACK_SLIDES.forEach((s) => { if (slides.length < 3) slides.push({ ...s }); });
  }
  return slides.filter((s) => pickLocalized(s.title, 'en') || pickLocalized(s.title, 'ar')).slice(0, 6);
};

const AUTOPLAY_MS = 5000;

// The card photo: ~84 % of a phone's width (a ~2× file on 3× screens), about
// 40 % of the stage on desktop. The same attributes go into the preload hint.
export const HERO_WIDTHS = [480, 720, 900, 1200];
export const HERO_SIZES = `(min-width: 1024px) 40vw, ${dprCappedSizes(84)}`;

export const heroImageAttrs = (image) => ({
  src: sizedImage(image, 900),
  srcset: sizedSrcSet(image, HERO_WIDTHS),
  sizes: HERO_SIZES,
});

// `load`: false for cards far from the one on screen; their photo is fetched
// when the carousel comes near them (off-screen slides would otherwise all
// download with the first one).
const HeroCard = ({ slide, index, palette, language, lead, load = true, onFirstImage }) => {
  const title = pickLocalized(slide.title, language);
  const line = pickLocalized(slide.line, language);
  const cta = pickLocalized(slide.cta, language) || (language === 'ar' ? 'تسوق الآن' : 'Shop now');
  const big = index === 0;
  return (
    <Link
      to={slide.href}
      className={`group grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-[22px] ${palette.bg} ${palette.text} outline-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-7px] focus-visible:outline-current ${
        big
          ? 'lg:grid-cols-[1.1fr_1fr] lg:grid-rows-1'
          // Side cards: text only at 1024-1279 (a photo column left ~110 px for the
          // words and clipped them), a narrower photo (34 %) from 1280 so the
          // headline and the button fit on one line in English and Arabic.
          : 'lg:grid-cols-1 lg:grid-rows-1 xl:grid-cols-[minmax(0,1fr)_34%]'
      }`}
      data-surface={palette.dark ? 'dark' : undefined}
      data-testid="hero-card"
    >
      <span className={`flex flex-col items-start px-5 pb-4 pt-5 ${big ? 'lg:justify-center lg:p-10' : 'lg:justify-center lg:p-7'}`}>
        {lead ? (
          <h2 className={`line-clamp-2 font-sans text-[2.25rem] font-bold leading-[0.95] tracking-[-0.03em] ${big ? 'lg:line-clamp-3 lg:text-[3.5rem] xl:text-[4rem]' : 'lg:text-[2rem] xl:text-[2.25rem]'}`}>{title}</h2>
        ) : (
          <span className={`line-clamp-2 font-sans text-[2.25rem] font-bold leading-[0.95] tracking-[-0.03em] ${big ? 'lg:line-clamp-3 lg:text-[3.5rem] xl:text-[4rem]' : 'lg:text-[2rem] xl:text-[2.25rem]'}`}>{title}</span>
        )}
        {line && <span className={`mt-1.5 line-clamp-1 max-w-[22rem] text-[14px] font-medium leading-snug ${palette.sub} ${big ? 'lg:mt-4 lg:line-clamp-2 lg:text-[18px]' : 'lg:line-clamp-2'}`}>{line}</span>}
        <span className={`mt-3 inline-flex min-h-[44px] items-center rounded-full px-6 text-[15px] font-bold transition-transform duration-200 motion-safe:group-hover:scale-[1.04] ${palette.cta} ${big ? 'lg:mt-5 lg:min-h-[52px] lg:px-8 lg:text-[16px]' : ''}`}>
          {cta}
        </span>
      </span>
      <span className={`relative mx-3 mb-3 block min-h-0 overflow-hidden rounded-[16px] bg-black/10 ${big ? 'lg:m-4 lg:ms-0' : 'lg:hidden xl:my-3 xl:me-3 xl:ms-0 xl:block'}`}>
        {slide.image && load && (
          <img
            src={heroImageAttrs(slide.image).src}
            srcSet={heroImageAttrs(slide.image).srcset}
            sizes={HERO_SIZES}
            alt=""
            width="900"
            height="1000"
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchpriority={index === 0 ? 'high' : 'low'}
            decoding="async"
            onLoad={index === 0 ? onFirstImage : undefined}
            onError={(event) => {
              event.currentTarget.style.visibility = 'hidden';
              if (index === 0 && onFirstImage) onFirstImage();
            }}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-[1.04]"
          />
        )}
      </span>
    </Link>
  );
};

const MarketHero = () => {
  const { t, language } = useLanguage();
  const reducedMotion = useReducedMotion();
  const [slides, setSlides] = useState(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(true);
  const trackRef = useRef(null);
  const rootRef = useRef(null);
  // Desktop shows three cards at once; phones one and the next peeking in.
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [reach, setReach] = useState(0);
  // The first photo is the largest paint: the others start once it is in.
  const [firstIn, setFirstIn] = useState(false);
  const onFirstImage = useCallback(() => {
    setFirstIn(true);
    markHeroPhotoDone();
  }, []);

  useEffect(() => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const signal = controller?.signal;
    let cancelled = false;
    Promise.all([
      getActiveFlashSales({ signal }).then((d) => withDeadlines(d)[0] || null).catch(() => null),
      getActiveBanners('hero', { signal }).then(normalizeBanners).catch(() => []),
      getCollections({ signal }).then((d) => (Array.isArray(d) ? d : d?.collections || [])).catch(() => []),
    ]).then(([sale, banners, collections]) => {
      if (cancelled) return;
      const built = buildHeroSlides({ sale, banners, collections });
      setSlides(built);
      writeHeroHint(built[0]?.image ? heroImageAttrs(built[0].image) : null);
    });
    return () => { cancelled = true; controller?.abort(); };
  }, []);

  // Autoplay only while the hero is on screen.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof window.IntersectionObserver !== 'function') return undefined;
    const io = new window.IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const cards = () => Array.from(trackRef.current?.children || []);

  const goTo = useCallback((index, smooth = true) => {
    const track = trackRef.current;
    const card = cards()[index];
    if (!track || !card || typeof track.scrollBy !== 'function') return;
    const tr = track.getBoundingClientRect();
    const cr = card.getBoundingClientRect();
    const delta = (cr.left + cr.width / 2) - (tr.left + tr.width / 2);
    track.scrollBy({ left: delta, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
  }, [reducedMotion]);

  // The card nearest the middle is the current one (swipes, RTL, keyboard).
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const tr = track.getBoundingClientRect();
    const mid = tr.left + tr.width / 2;
    let best = 0;
    let bestDist = Infinity;
    cards().forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setCurrent(best);
  };

  const isDesktop = () => typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches;
  const count = slides?.length || 0;
  const autoplay = count > 1 && !paused && !interacting && !reducedMotion && visible;

  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setInterval(() => {
      if (isDesktop()) return;
      goTo((current + 1) % count);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [autoplay, count, current, goTo]);

  // Photos load for every card seen so far plus the next one (desktop: the
  // three on screen), after the first photo; never all slides at once.
  useEffect(() => {
    if (!firstIn && current === 0) return;
    setReach((r) => Math.max(r, current + 1, desktop ? 2 : 1));
  }, [current, desktop, firstIn]);
  // A first slide without a photo holds nothing back.
  useEffect(() => {
    if (slides && !slides[0]?.image) onFirstImage();
  }, [slides, onFirstImage]);

  const loading = slides === null;
  const list = loading ? [0, 1, 2] : slides;

  return (
    <section
      ref={rootRef}
      className="pt-3 lg:luxury-stage-wide lg:pt-5"
      aria-roledescription="carousel"
      aria-label={t({ en: 'Offers and edits', ar: 'العروض والتشكيلات' })}
      aria-busy={loading || undefined}
      data-testid="market-hero"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setInteracting(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setInteracting(false); }}
      onPointerDown={() => setInteracting(true)}
      onFocus={() => setInteracting(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setInteracting(false); }}
    >
      <ul
        ref={trackRef}
        onScroll={onScroll}
        className="anum-no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-3 px-3 lg:grid lg:h-[min(420px,62vh)] lg:grid-cols-3 lg:grid-rows-2 lg:gap-4 lg:overflow-visible lg:px-0 xl:h-[min(460px,62vh)]"
      >
        {list.map((slide, i) => {
          const palette = HERO_PALETTES[i % HERO_PALETTES.length];
          return (
            <li
              key={loading ? `sk-${i}` : slide.id}
              className={`aspect-square w-[84%] shrink-0 snap-center sm:w-[58%] md:aspect-[5/4] lg:aspect-auto lg:w-auto ${
                i === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              } ${i > 2 ? 'lg:hidden' : ''}`}
              aria-roledescription="slide"
              aria-label={loading ? undefined : t({ en: `${i + 1} of ${count}`, ar: `${i + 1} من ${count}` })}
            >
              {loading ? (
                <div className={`h-full w-full animate-pulse rounded-[22px] motion-reduce:animate-none ${palette.bg}`} aria-hidden="true" />
              ) : (
                <HeroCard slide={slide} index={i} palette={palette} language={language} lead={i === 0} load={i <= reach} onFirstImage={onFirstImage} />
              )}
            </li>
          );
        })}
      </ul>

      {/* The dots row's height (44 px, phones) is kept while the slides load,
          so the blocks below do not move down when it appears (layout shift). */}
      {loading && <div className="h-11 lg:hidden" aria-hidden="true" data-testid="hero-dots-placeholder" />}
      {!loading && count > 1 && (
        <div className="flex items-center justify-center gap-1 lg:hidden">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => { setPaused(true); goTo(i); }}
              aria-label={t({ en: `Show slide ${i + 1}`, ar: `عرض الشريحة ${i + 1}` })}
              aria-current={i === current ? 'true' : undefined}
              className="flex h-11 w-6 items-center justify-center"
            >
              <span aria-hidden="true" className={`block h-2 rounded-full transition-all duration-300 motion-reduce:transition-none ${i === current ? 'w-5 bg-crimson' : 'w-2 bg-stone/40'}`} />
            </button>
          ))}
          {!reducedMotion && (
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? t({ en: 'Play slides', ar: 'تشغيل الشرائح' }) : t({ en: 'Pause slides', ar: 'إيقاف الشرائح' })}
              className="ms-1 flex h-11 w-11 items-center justify-center text-stone hover:text-foreground"
            >
              {paused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default MarketHero;
